import { useRef, useState } from "react";
import {
  Text,
  Paper,
  Flex,
  Grid,
  Box,
  Group,
  ThemeIcon,
  Select,
  Loader,
  Badge,
  RingProgress,
  Divider,
  Stack,
  ActionIcon,
  Tooltip,
  Button,
  Menu,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useApiUrl, useCustom } from "@refinedev/core";
import html2canvas from "html2canvas";
import {
  IconChartBar,
  IconMail,
  IconStar,
  IconSend,
  IconAlertCircle,
  IconCalendarEvent,
  IconCircleCheck,
  IconPhoto,
  IconFileText,
  IconShieldCheck,
  IconArrowRight,
  IconMailOff,
  IconX,
  IconDownload,
  IconTable,
  IconCamera,
} from "@tabler/icons";
import {
  SOURCE_OPTIONS,
  SOURCE_COLORS,
  SOURCE_LABELS,
} from "entities/review_request";

/* ═══════════════════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════════════════ */

type AnalyticsResponse = {
  filters: {
    source: string | null;
    start_date: string | null;
    end_date: string | null;
  };
  delivery_and_system_health: {
    total_review_requests: number;
    scheduled: number;
    sent: number;
    failed: number;
    total_attempted: number;
    deliverability_rate: number;
    deliverability_target: number;
    deliverability_meets_target: boolean;
    unsubscribed: number;
    unsubscribe_rate: number;
    unsubscribe_target: number;
    unsubscribe_meets_target: boolean;
  };
  business_outcome_and_conversion: {
    sent: number;
    total_reviews: number;
    review_submission_rate: number;
    review_quality: {
      with_body: number;
      high_quality: number;
      high_quality_rate: number;
      with_photos: number;
      with_photos_rate: number;
    };
    rating_distribution: Record<string, number>;
    average_rating: number;
  };
};

/* ═══════════════════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════════════════ */

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const RATING_HEX: Record<number, string> = {
  1: "#e03131",
  2: "#e8590c",
  3: "#f59f00",
  4: "#82c91e",
  5: "#2f9e44",
};

/* ═══════════════════════════════════════════════════════════════════════════
   Export helpers
   ═══════════════════════════════════════════════════════════════════════════ */

function exportCsv(data: AnalyticsResponse) {
  const h = data.delivery_and_system_health;
  const b = data.business_outcome_and_conversion;
  const q = b.review_quality;

  const rows: string[][] = [
    ["Review Request Analytics Report"],
    ["Generated", new Date().toISOString()],
    [],
    ["Filters"],
    ["Source", data.filters.source || "All"],
    ["Start Date", data.filters.start_date || "All time"],
    ["End Date", data.filters.end_date || "All time"],
    [],
    ["Delivery & System Health"],
    ["Metric", "Value"],
    ["Total Review Requests", String(h.total_review_requests)],
    ["Scheduled", String(h.scheduled)],
    ["Sent", String(h.sent)],
    ["Failed", String(h.failed)],
    ["Total Attempted", String(h.total_attempted)],
    ["Deliverability Rate (%)", String(h.deliverability_rate)],
    ["Deliverability Target (%)", String(h.deliverability_target)],
    ["Meets Deliverability Target", String(h.deliverability_meets_target)],
    ["Unsubscribed", String(h.unsubscribed)],
    ["Unsubscribe Rate (%)", String(h.unsubscribe_rate)],
    ["Unsubscribe Target (%)", String(h.unsubscribe_target)],
    ["Meets Unsubscribe Target", String(h.unsubscribe_meets_target)],
    [],
    ["Business Outcome & Conversion"],
    ["Metric", "Value"],
    ["Sent", String(b.sent)],
    ["Total Reviews", String(b.total_reviews)],
    ["Review Submission Rate (%)", String(b.review_submission_rate)],
    ["Average Rating", String(b.average_rating)],
    [],
    ["Rating Distribution"],
    ["Stars", "Count"],
    ["5 Stars", String(b.rating_distribution["5"] || 0)],
    ["4 Stars", String(b.rating_distribution["4"] || 0)],
    ["3 Stars", String(b.rating_distribution["3"] || 0)],
    ["2 Stars", String(b.rating_distribution["2"] || 0)],
    ["1 Star", String(b.rating_distribution["1"] || 0)],
    [],
    ["Review Quality"],
    ["Metric", "Count", "Rate (%)"],
    ["With Body (50+ words)", String(q.with_body), String(b.total_reviews > 0 ? ((q.with_body / b.total_reviews) * 100).toFixed(2) : "0.00")],
    ["With Photos", String(q.with_photos), String(q.with_photos_rate)],
    ["High Quality", String(q.high_quality), String(q.high_quality_rate)],
  ];

  const csvContent = rows
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `review_request_analytics_${formatDate(new Date())}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

async function exportPng(element: HTMLElement) {
  const canvas = await html2canvas(element, {
    backgroundColor: null,
    scale: 2,
    useCORS: true,
  });
  const url = canvas.toDataURL("image/png");
  const link = document.createElement("a");
  link.href = url;
  link.download = `review_request_analytics_${formatDate(new Date())}.png`;
  link.click();
}

/* ═══════════════════════════════════════════════════════════════════════════
   KPI metric card
   ═══════════════════════════════════════════════════════════════════════════ */

function MetricCard({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  color: string;
}) {
  return (
    <Paper
      p="md"
      radius="md"
      sx={(theme) => ({
        border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]
          }`,
        height: "100%",
      })}
    >
      <Group spacing="sm" noWrap>
        <ThemeIcon size={40} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Text size="xl" weight={800} sx={{ lineHeight: 1.1, fontFamily: "monospace" }}>
            {value}
          </Text>
          <Text size="xs" color="dimmed" weight={500} mt={2}>
            {label}
          </Text>
          {sub && (
            <Text size={10} color="dimmed" mt={1}>
              {sub}
            </Text>
          )}
        </Box>
      </Group>
    </Paper>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Section header
   ═══════════════════════════════════════════════════════════════════════════ */

function SectionHeader({
  icon,
  title,
  color,
}: {
  icon: React.ReactNode;
  title: string;
  color: string;
}) {
  return (
    <Group spacing="sm" mb="md">
      <ThemeIcon size={28} radius="md" variant="light" color={color}>
        {icon}
      </ThemeIcon>
      <Text size="xs" weight={700} transform="uppercase" sx={{ letterSpacing: 0.8 }}>
        {title}
      </Text>
    </Group>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Target gauge — ring progress with target comparison
   ═══════════════════════════════════════════════════════════════════════════ */

function TargetGauge({
  label,
  rate,
  target,
  meetsTarget,
  inverse,
}: {
  label: string;
  rate: number;
  target: number;
  meetsTarget: boolean;
  inverse?: boolean;
}) {
  const displayRate = Math.min(rate, 100);
  const ringColor = meetsTarget ? "green" : "red";

  return (
    <Paper
      p="md"
      radius="md"
      sx={(theme) => ({
        border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]
          }`,
        height: "100%",
      })}
    >
      <Flex align="center" gap="md">
        <RingProgress
          size={80}
          thickness={8}
          roundCaps
          sections={[{ value: displayRate, color: ringColor }]}
          label={
            <Text size="xs" weight={800} align="center" sx={{ fontFamily: "monospace" }}>
              {rate}%
            </Text>
          }
        />
        <Box sx={{ flex: 1 }}>
          <Text size="sm" weight={700}>
            {label}
          </Text>
          <Group spacing={4} mt={4}>
            <Badge
              variant="light"
              color={meetsTarget ? "green" : "red"}
              size="xs"
              radius="xl"
            >
              {meetsTarget ? "On target" : "Below target"}
            </Badge>
          </Group>
          <Text size={10} color="dimmed" mt={4}>
            Target: {inverse ? "≤" : "≥"} {target}%
          </Text>
        </Box>
      </Flex>
    </Paper>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Rating distribution bar chart
   ═══════════════════════════════════════════════════════════════════════════ */

function RatingDistribution({
  distribution,
  averageRating,
}: {
  distribution: Record<string, number>;
  averageRating: number;
}) {
  const totalReviews = Object.values(distribution).reduce((s, v) => s + v, 0);
  const maxCount = Math.max(...Object.values(distribution), 1);

  return (
    <Paper
      p="lg"
      radius="md"
      sx={(theme) => ({
        border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]
          }`,
        height: "100%",
      })}
    >
      <Group position="apart" mb="md">
        <Box>
          <Text size="xs" weight={700} transform="uppercase" sx={{ letterSpacing: 0.8 }}>
            Rating Distribution
          </Text>
          <Text size="xs" color="dimmed" mt={2}>
            {formatNumber(totalReviews)} total reviews
          </Text>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Text weight={800} sx={{ fontSize: 28, fontFamily: "monospace", lineHeight: 1 }}>
            {averageRating.toFixed(1)}
          </Text>
          <Text size={10} color="dimmed" mt={2}>
            avg rating
          </Text>
        </Box>
      </Group>

      <Stack spacing={8}>
        {[5, 4, 3, 2, 1].map((star) => {
          const count = distribution[String(star)] || 0;
          const pct = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
          const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;

          return (
            <Group key={star} spacing="sm" noWrap>
              <Group spacing={4} noWrap sx={{ width: 28, flexShrink: 0 }}>
                <Text size="xs" weight={700} sx={{ fontFamily: "monospace" }}>
                  {star}
                </Text>
                <IconStar size={11} color={RATING_HEX[star]} fill={RATING_HEX[star]} />
              </Group>

              <Box
                sx={(theme) => ({
                  flex: 1,
                  height: 20,
                  borderRadius: theme.radius.sm,
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.colors.dark[6]
                      : theme.colors.gray[1],
                  overflow: "hidden",
                  position: "relative",
                })}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: `${barWidth}%`,
                    backgroundColor: RATING_HEX[star],
                    borderRadius: "inherit",
                    transition: "width 600ms ease",
                    opacity: 0.85,
                  }}
                />
              </Box>

              <Text
                size={11}
                weight={600}
                color="dimmed"
                sx={{ width: 60, textAlign: "right", fontFamily: "monospace", flexShrink: 0 }}
              >
                {formatNumber(count)} ({pct.toFixed(0)}%)
              </Text>
            </Group>
          );
        })}
      </Stack>
    </Paper>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Delivery funnel — visual pipeline
   ═══════════════════════════════════════════════════════════════════════════ */

function DeliveryFunnel({
  total,
  scheduled,
  sent,
  failed,
  reviews,
}: {
  total: number;
  scheduled: number;
  sent: number;
  failed: number;
  reviews: number;
}) {
  const steps = [
    { label: "Total Requests", value: total, color: "blue", icon: <IconMail size={14} /> },
    { label: "Scheduled", value: scheduled, color: "blue", icon: <IconCalendarEvent size={14} /> },
    { label: "Sent", value: sent, color: "green", icon: <IconSend size={14} /> },
    { label: "Reviews", value: reviews, color: "yellow", icon: <IconStar size={14} /> },
  ];

  return (
    <Paper
      p="lg"
      radius="md"
      sx={(theme) => ({
        border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]
          }`,
      })}
    >
      <Text size="xs" weight={700} transform="uppercase" sx={{ letterSpacing: 0.8 }} mb="md">
        Conversion Funnel
      </Text>

      <Flex align="center" gap={0}>
        {steps.map((step, i) => (
          <Flex key={step.label} align="center" sx={{ flex: 1 }}>
            <Box sx={{ flex: 1, textAlign: "center" }}>
              <ThemeIcon size={32} radius="xl" variant="light" color={step.color} mx="auto">
                {step.icon}
              </ThemeIcon>
              <Text weight={800} sx={{ fontSize: 18, fontFamily: "monospace", lineHeight: 1 }} mt={8}>
                {formatNumber(step.value)}
              </Text>
              <Text size={10} color="dimmed" weight={500} mt={4}>
                {step.label}
              </Text>
            </Box>
            {i < steps.length - 1 && (
              <Box sx={{ flexShrink: 0, opacity: 0.3 }}>
                <IconArrowRight size={16} />
              </Box>
            )}
          </Flex>
        ))}
      </Flex>

      {/* Failed callout */}
      {failed > 0 && (
        <Paper
          mt="md"
          p="xs"
          radius="sm"
          sx={(theme) => ({
            backgroundColor:
              theme.colorScheme === "dark"
                ? theme.fn.rgba(theme.colors.red[9], 0.08)
                : theme.colors.red[0],
            border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.red[9] : theme.colors.red[2]
              }`,
          })}
        >
          <Group spacing="xs" noWrap>
            <ThemeIcon size={20} radius="xl" variant="light" color="red">
              <IconAlertCircle size={12} />
            </ThemeIcon>
            <Text size="xs" weight={600}>
              {formatNumber(failed)} failed
            </Text>
            <Text size={10} color="dimmed">
              ({total > 0 ? ((failed / total) * 100).toFixed(1) : 0}% of total)
            </Text>
          </Group>
        </Paper>
      )}
    </Paper>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Quality breakdown cards
   ═══════════════════════════════════════════════════════════════════════════ */

function QualityCard({
  icon,
  label,
  count,
  rate,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  rate: number;
  color: string;
}) {
  return (
    <Paper
      p="md"
      radius="md"
      sx={(theme) => ({
        border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]
          }`,
        height: "100%",
      })}
    >
      <Group spacing="sm" noWrap>
        <ThemeIcon size={32} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Box sx={{ flex: 1 }}>
          <Text size="lg" weight={800} sx={{ fontFamily: "monospace", lineHeight: 1 }}>
            {formatNumber(count)}
          </Text>
          <Text size="xs" color="dimmed" weight={500} mt={2}>
            {label}
          </Text>
        </Box>
        <Badge variant="light" color={color} size="sm" radius="xl" sx={{ fontWeight: 700 }}>
          {rate.toFixed(1)}%
        </Badge>
      </Group>
    </Paper>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

export const ReviewRequestAnalytics: React.FC = () => {
  const apiUrl = useApiUrl();
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [source, setSource] = useState<string | null>("JAPAN_HAUL");
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  });

  // Build query params
  const query: Record<string, string> = {};
  if (source) query.source = source;
  if (startDate) query.start_date = formatDate(startDate);
  if (endDate) query.end_date = formatDate(endDate);

  const { data: analyticsData, isFetching } = useCustom<AnalyticsResponse>({
    url: `${apiUrl}/review_request_analytics`,
    method: "get",
    config: { query },
    queryOptions: { enabled: true },
  });

  const d = analyticsData?.data;
  const health = d?.delivery_and_system_health;
  const business = d?.business_outcome_and_conversion;

  const hasFilters = source || startDate || endDate;

  const clearFilters = () => {
    setSource(null);
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <Box>
      {/* ── Header ──────────────────────────────────────────────────── */}
      <Paper
        p="lg"
        radius="md"
        mb="lg"
        sx={(theme) => ({
          background:
            theme.colorScheme === "dark"
              ? `linear-gradient(135deg, ${theme.colors.dark[7]} 0%, ${theme.fn.rgba(theme.colors.pink[9], 0.12)} 100%)`
              : `linear-gradient(135deg, ${theme.white} 0%, ${theme.colors.pink[0]} 100%)`,
          border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.pink[1]
            }`,
        })}
      >
        <Group position="apart" noWrap>
          <Group spacing="md" noWrap>
            <ThemeIcon size={48} radius="xl" variant="light" color="pink">
              <IconChartBar size={24} />
            </ThemeIcon>
            <Box>
              <Text size="lg" weight={700}>
                Review Request Analytics
              </Text>
              <Text size="sm" color="dimmed">
                Delivery performance, conversion rates, and review quality metrics
              </Text>
            </Box>
          </Group>
          <Group spacing="sm" noWrap>
            {isFetching && <Loader size="sm" color="pink" />}
            {d && (
              <Menu shadow="md" radius="md" withinPortal position="bottom-end">
                <Menu.Target>
                  <Button
                    variant="light"
                    color="pink"
                    size="xs"
                    radius="md"
                    leftIcon={<IconDownload size={14} />}
                  >
                    Export
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<IconTable size={14} />}
                    onClick={() => exportCsv(d)}
                  >
                    Export as CSV
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconCamera size={14} />}
                    onClick={() => dashboardRef.current && exportPng(dashboardRef.current)}
                  >
                    Export as PNG
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>
      </Paper>

      {/* ── Filters ─────────────────────────────────────────────────── */}
      <Paper
        p="md"
        radius="md"
        mb="lg"
        sx={(theme) => ({
          border: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]
            }`,
        })}
      >
        <Group position="apart" mb="xs">
          <Text size="xs" weight={700} transform="uppercase" sx={{ letterSpacing: 0.8 }}>
            Filters
          </Text>
          {hasFilters && (
            <Tooltip label="Clear all filters" withinPortal>
              <ActionIcon variant="light" color="gray" size="sm" radius="xl" onClick={clearFilters}>
                <IconX size={12} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
        <Grid gutter="md">
          <Grid.Col span={4}>
            <Select
              label="Source"
              placeholder="All sources"
              data={SOURCE_OPTIONS}
              value={source}
              onChange={setSource}
              clearable
              size="xs"
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <DatePicker
              label="Start date"
              placeholder="From..."
              value={startDate}
              onChange={setStartDate}
              clearable
              size="xs"
              withinPortal
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <DatePicker
              label="End date"
              placeholder="To..."
              value={endDate}
              onChange={setEndDate}
              clearable
              size="xs"
              withinPortal
            />
          </Grid.Col>
        </Grid>

        {/* Active filter chips */}
        {hasFilters && (
          <Group spacing={6} mt="sm">
            {source && (
              <Badge variant="outline" color="blue" size="sm" radius="xl">
                Source: {SOURCE_LABELS[source] || source}
              </Badge>
            )}
            {startDate && (
              <Badge variant="outline" color="blue" size="sm" radius="xl">
                From: {formatDate(startDate)}
              </Badge>
            )}
            {endDate && (
              <Badge variant="outline" color="blue" size="sm" radius="xl">
                To: {formatDate(endDate)}
              </Badge>
            )}
          </Group>
        )}
      </Paper>

      {/* Loading state */}
      {isFetching && !d && (
        <Flex justify="center" py={80}>
          <Loader size="lg" color="pink" />
        </Flex>
      )}

      {d && health && business && (
        <Box ref={dashboardRef}>
          {/* ═══════════════════════════════════════════════════════════
             SECTION 1: Delivery & System Health
             ═══════════════════════════════════════════════════════════ */}
          <SectionHeader
            icon={<IconSend size={14} />}
            title="Delivery & System Health"
            color="blue"
          />

          {/* KPI cards */}
          <Grid gutter="md" mb="md">
            <Grid.Col span={3}>
              <MetricCard
                icon={<IconMail size={18} />}
                label="Total Requests"
                value={formatNumber(health.total_review_requests)}
                color="blue"
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <MetricCard
                icon={<IconCalendarEvent size={18} />}
                label="Scheduled"
                value={formatNumber(health.scheduled)}
                sub="Queued for delivery"
                color="blue"
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <MetricCard
                icon={<IconSend size={18} />}
                label="Sent"
                value={formatNumber(health.sent)}
                sub={`${health.total_attempted > 0 ? ((health.sent / health.total_attempted) * 100).toFixed(1) : 0}% of attempted`}
                color="green"
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <MetricCard
                icon={<IconAlertCircle size={18} />}
                label="Failed"
                value={formatNumber(health.failed)}
                sub={`${health.total_attempted > 0 ? ((health.failed / health.total_attempted) * 100).toFixed(1) : 0}% of attempted`}
                color="red"
              />
            </Grid.Col>
          </Grid>

          {/* Target gauges */}
          <Grid gutter="md" mb="lg">
            <Grid.Col span={4}>
              <TargetGauge
                label="Deliverability Rate"
                rate={health.deliverability_rate}
                target={health.deliverability_target}
                meetsTarget={health.deliverability_meets_target}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <TargetGauge
                label="Unsubscribe Rate"
                rate={health.unsubscribe_rate}
                target={health.unsubscribe_target}
                meetsTarget={health.unsubscribe_meets_target}
                inverse
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <MetricCard
                icon={<IconMailOff size={18} />}
                label="Unsubscribed"
                value={formatNumber(health.unsubscribed)}
                sub={`${health.unsubscribe_rate}% of sent emails`}
                color="gray"
              />
            </Grid.Col>
          </Grid>

          <Divider
            mb="lg"
            sx={(theme) => ({
              borderColor:
                theme.colorScheme === "dark"
                  ? theme.colors.dark[5]
                  : theme.colors.gray[2],
            })}
          />

          {/* ═══════════════════════════════════════════════════════════
             SECTION 2: Business Outcome & Conversion
             ═══════════════════════════════════════════════════════════ */}
          <SectionHeader
            icon={<IconStar size={14} />}
            title="Business Outcome & Conversion"
            color="yellow"
          />

          {/* Conversion KPIs */}
          <Grid gutter="md" mb="md">
            <Grid.Col span={4}>
              <MetricCard
                icon={<IconSend size={18} />}
                label="Emails Sent"
                value={formatNumber(business.sent)}
                color="green"
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <MetricCard
                icon={<IconStar size={18} />}
                label="Reviews Received"
                value={formatNumber(business.total_reviews)}
                color="yellow"
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <MetricCard
                icon={<IconCircleCheck size={18} />}
                label="Submission Rate"
                value={`${business.review_submission_rate}%`}
                sub={`${formatNumber(business.total_reviews)} of ${formatNumber(business.sent)} sent`}
                color="teal"
              />
            </Grid.Col>
          </Grid>

          {/* Funnel + Rating distribution */}
          <Grid gutter="md" mb="md">
            <Grid.Col span={7}>
              <DeliveryFunnel
                total={health.total_review_requests}
                scheduled={health.scheduled}
                sent={health.sent}
                failed={health.failed}
                reviews={business.total_reviews}
              />
            </Grid.Col>
            <Grid.Col span={5}>
              <RatingDistribution
                distribution={business.rating_distribution}
                averageRating={business.average_rating}
              />
            </Grid.Col>
          </Grid>

          {/* Review quality */}
          <Text size="xs" weight={700} transform="uppercase" sx={{ letterSpacing: 0.8 }} mb="sm">
            Review Quality
          </Text>
          <Grid gutter="md">
            <Grid.Col span={4}>
              <QualityCard
                icon={<IconFileText size={16} />}
                label="With Body (50+ words)"
                count={business.review_quality.with_body}
                rate={
                  business.total_reviews > 0
                    ? (business.review_quality.with_body / business.total_reviews) * 100
                    : 0
                }
                color="blue"
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <QualityCard
                icon={<IconPhoto size={16} />}
                label="With Photos"
                count={business.review_quality.with_photos}
                rate={business.review_quality.with_photos_rate}
                color="cyan"
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <QualityCard
                icon={<IconShieldCheck size={16} />}
                label="High Quality"
                count={business.review_quality.high_quality}
                rate={business.review_quality.high_quality_rate}
                color="green"
              />
            </Grid.Col>
          </Grid>
        </Box>
      )}
    </Box>
  );
};
